import type { FormEvent } from 'react';
import { memo, useState } from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { GlobalState } from '../../global/types';

import { isValidEmail } from '../../util/cx';

import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';

import TelegrafLogo from '../cx/common/TelegrafLogo';
import Button from '../ui/Button';
import InputText from '../ui/InputText';

import styles from './AuthEmail.module.scss';

type StateProps = Pick<GlobalState['auth'], 'isLoading' | 'errorKey' | 'email'>;

// Slack-style sign-in: one screen for both new and returning users
const AuthEmail = ({ isLoading, errorKey, email: savedEmail }: StateProps) => {
  const { setAuthEmail, clearAuthErrorKey } = getActions();

  const [email, setEmail] = useState(savedEmail || '');

  const lang = useLang();

  const isEmailValid = isValidEmail(email);

  const handleChange = useLastCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (errorKey) clearAuthErrorKey();
    setEmail(e.target.value);
  });

  const handleSubmit = useLastCallback((e: FormEvent) => {
    e.preventDefault();
    if (!isEmailValid || isLoading) return;

    setAuthEmail({ email });
  });

  return (
    <div className={styles.root}>
      <TelegrafLogo className={styles.logo} withWordmark />
      <h1 className={styles.title}>{lang('CxAuthTitle')}</h1>
      <p className={styles.subtitle}>
        {lang('CxAuthSubtitle', undefined, { withNodes: true, withMarkdown: true })}
      </p>
      <form className={styles.form} onSubmit={handleSubmit}>
        <InputText
          id="sign-in-email"
          label={lang('CxAuthEmailPlaceholder')}
          value={email}
          inputMode="email"
          autoComplete="email"
          error={errorKey && lang.withRegular(errorKey)}
          autoFocus
          onChange={handleChange}
        />
        <Button className={styles.submit} type="submit" disabled={!isEmailValid} isLoading={isLoading}>
          {lang('CxAuthContinue')}
        </Button>
      </form>
      <p className={styles.hint}>{lang('CxAuthNewHint')}</p>
    </div>
  );
};

export default memo(withGlobal((global): Complete<StateProps> => {
  const { isLoading, errorKey, email } = global.auth;

  return {
    isLoading,
    errorKey,
    email,
  };
})(AuthEmail));
